<template>
  <section class="spt" :class="{ 'spt--past': readonly }">
    <header v-if="!compact" class="spt__head">
      <div class="spt__heading">
        <h2 class="spt__name">{{ plan.name }}</h2>
        <p v-if="plan.description" class="spt__desc">{{ plan.description }}</p>
      </div>
      <div class="spt__meta">
        <span class="spt__pill">{{ deadlineText }}</span>
        <span v-if="statusText" class="spt__pill spt__pill--status">
          {{ statusText }}
        </span>
      </div>
    </header>

    <div class="spt__progress">
      <div
        class="spt__bar"
        role="progressbar"
        :aria-valuenow="plan.progress.percent"
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <span
          class="spt__fill"
          :style="{ width: `${plan.progress.percent}%` }"
        />
      </div>
      <span class="spt__count">
        {{
          $t("studyPlan.progressOf", {
            done: plan.progress.done,
            total: plan.progress.total,
          })
        }}
        · {{ plan.progress.percent }}%
      </span>
    </div>

    <ol class="spt__trail">
      <li
        v-for="item in plan.items"
        :key="item.id"
        class="spt__stop"
        :class="{
          'spt__stop--done': item.done,
          'spt__stop--next': item.id === nextId,
        }"
      >
        <span class="spt__node" aria-hidden="true">
          <v-icon v-if="item.done" size="14" color="#fff">mdi-check</v-icon>
        </span>

        <div class="spt__card">
          <div class="spt__kind">
            <v-icon size="16" class="spt__kind-icon">{{ iconOf(item) }}</v-icon>
            <span>{{ $t(`studyPlan.kind.${item.type}`) }}</span>
            <span v-if="item.id === nextId" class="spt__next">
              {{ $t("studyPlan.next") }}
            </span>
          </div>

          <!-- Aula: conta sozinha (aula marcada + exercícios finalizados). -->
          <template v-if="item.type === 'lesson'">
            <h3 class="spt__title">{{ item.title }}</h3>

            <p v-if="item.unavailable" class="spt__note">
              {{ $t("studyPlan.lessonUnavailable") }}
            </p>
            <template v-else>
              <ul class="spt__facts">
                <li :class="{ 'spt__fact--on': item.lessonDone }">
                  <v-icon size="16">{{
                    item.lessonDone ? "mdi-check-circle" : "mdi-circle-outline"
                  }}</v-icon>
                  {{
                    item.lessonDone
                      ? $t("studyPlan.lessonDone")
                      : $t("studyPlan.lessonOpen")
                  }}
                </li>
                <li
                  v-if="item.exercises.total"
                  :class="{
                    'spt__fact--on':
                      item.exercises.done >= item.exercises.total,
                  }"
                >
                  <v-icon size="16">{{
                    item.exercises.done >= item.exercises.total
                      ? "mdi-check-circle"
                      : "mdi-circle-outline"
                  }}</v-icon>
                  {{
                    $t("studyPlan.exercises", {
                      done: item.exercises.done,
                      total: item.exercises.total,
                    })
                  }}
                </li>
              </ul>
              <router-link
                class="spt__btn"
                :to="`/connect/aluno/curso/${item.courseClassId}`"
              >
                {{ $t("studyPlan.openMaterial") }}
              </router-link>
            </template>
          </template>

          <!-- Atividade com link: o aluno marca por conta própria. -->
          <template v-else-if="item.type === 'link'">
            <h3 class="spt__title">{{ item.description }}</h3>
            <div class="spt__actions">
              <a
                class="spt__btn"
                :href="item.url"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ $t("studyPlan.openLink") }}
              </a>
              <v-checkbox
                v-if="!readonly"
                :input-value="item.done"
                :label="$t('studyPlan.markDone')"
                :loading="busyId === item.id"
                :disabled="busyId === item.id"
                color="#a826e1"
                hide-details
                dense
                class="spt__check"
                @change="setDone(item, $event)"
              />
              <span v-else-if="item.done" class="spt__fact--on">
                {{ $t("studyPlan.markedDone") }}
              </span>
            </div>
          </template>

          <!-- Texto: rascunho, envio e a correção da Pitch. -->
          <template v-else>
            <h3 class="spt__title spt__title--prompt">{{ item.prompt }}</h3>

            <template v-if="!item.submittedAt && !readonly">
              <v-textarea
                v-model="drafts[item.id]"
                :placeholder="$t('studyPlan.writePlaceholder')"
                rows="4"
                auto-grow
                outlined
                counter="10000"
                maxlength="10000"
                color="#a826e1"
                class="spt__text"
              />
              <div class="spt__actions">
                <button
                  type="button"
                  class="spt__btn spt__btn--ghost"
                  :disabled="busyId === item.id"
                  @click="saveText(item, false)"
                >
                  {{ $t("studyPlan.saveDraft") }}
                </button>
                <button
                  type="button"
                  class="spt__btn"
                  :disabled="
                    busyId === item.id || !(drafts[item.id] || '').trim()
                  "
                  @click="saveText(item, true)"
                >
                  {{ $t("studyPlan.submit") }}
                </button>
              </div>
              <p class="spt__hint">{{ $t("studyPlan.submitHint") }}</p>
            </template>

            <template v-else>
              <p v-if="item.response" class="spt__answer">
                {{ item.response }}
              </p>
              <p v-if="item.submittedAt && !item.gradedAt" class="spt__note">
                {{
                  $t("studyPlan.submitted", {
                    date: formatDateTime(item.submittedAt),
                  })
                }}
              </p>
              <div v-if="item.gradedAt" class="spt__grade">
                <span v-if="item.score !== null" class="spt__score">
                  {{ $t("studyPlan.score") }} <b>{{ item.score }}</b>
                </span>
                <p v-if="item.feedback" class="spt__feedback">
                  <span class="spt__feedback-k">
                    {{ $t("studyPlan.feedback") }}
                  </span>
                  {{ item.feedback }}
                </p>
              </div>
            </template>
          </template>
        </div>
      </li>
    </ol>
  </section>
</template>

<script>
import { format } from "date-fns";
import pitchCustomerApi from "@/shared/pitchCustomerApi";

const ICONS = {
  lesson: "mdi-book-open-variant",
  link: "mdi-link-variant",
  writing: "mdi-pencil-outline",
};

const dayOf = (value) => String(value || "").slice(0, 10);

// Um plano de estudos como uma trilha de etapas. O que o aluno faz aqui é
// marcar o link como feito e escrever o texto; aula e exercício entram
// sozinhos. Sem tom de cobrança: o prazo é informação, não alerta.
export default {
  name: "StudyPlanTrail",

  props: {
    plan: { type: Object, required: true },
    // Planos anteriores: só leitura.
    readonly: { type: Boolean, default: false },
    // Sem o cabeçalho (o pai já mostra o nome do plano).
    compact: { type: Boolean, default: false },
  },

  data() {
    return { drafts: {}, busyId: "" };
  },

  computed: {
    // A primeira etapa ainda aberta: onde a pessoa continua.
    nextId() {
      if (this.readonly || this.plan.completedAt) return "";
      return (this.plan.items.find((item) => !item.done) || {}).id || "";
    },

    deadlineText() {
      const date = dayOf(this.plan.deadline).split("-").reverse().join("/");
      const base = this.$t("studyPlan.deadlineUntil", { date });
      if (this.readonly || this.plan.completedAt) return base;

      const days = this.daysLeft();
      if (days === 0) return `${base} · ${this.$t("studyPlan.lastDay")}`;
      if (days === 1) return `${base} · ${this.$t("studyPlan.oneDayLeft")}`;
      return `${base} · ${this.$t("studyPlan.daysLeft", { n: days })}`;
    },

    statusText() {
      if (this.plan.completedAt) {
        return this.plan.completedOnTime
          ? this.$t("studyPlan.doneOnTime")
          : this.$t("studyPlan.doneLate");
      }
      return this.plan.phase === "expired" ? this.$t("studyPlan.expired") : "";
    },
  },

  watch: {
    // O rascunho começa pelo que está salvo. Quando o plano é atualizado por
    // outra ação (marcar um link), o texto ainda não salvo é mantido.
    plan: {
      immediate: true,
      handler(plan) {
        const drafts = {};
        (plan.items || []).forEach((item) => {
          if (item.type === "writing") {
            drafts[item.id] =
              item.id in this.drafts
                ? this.drafts[item.id]
                : item.response || "";
          }
        });
        this.drafts = drafts;
      },
    },
  },

  methods: {
    iconOf(item) {
      return ICONS[item.type];
    },

    // Dias de calendário até o prazo, no relógio do aluno.
    daysLeft() {
      const [year, month, day] = dayOf(this.plan.deadline)
        .split("-")
        .map(Number);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const deadline = new Date(year, month - 1, day);
      return Math.max(0, Math.round((deadline - today) / 86400000));
    },

    formatDateTime(value) {
      return format(new Date(value), "dd/MM/yyyy HH:mm");
    },

    failed(err) {
      const message = err?.response?.data?.message;
      this.$root.$emit("display-alert", {
        text: Array.isArray(message)
          ? message.join(" ")
          : message || this.$t("studyPlan.saveError"),
        type: 1,
      });
    },

    setDone(item, done) {
      this.busyId = item.id;
      pitchCustomerApi
        .setStudyPlanItemDone(this.plan.id, item.id, !!done)
        .then((res) => this.$emit("update", res.data.data))
        .catch(this.failed)
        .finally(() => {
          this.busyId = "";
        });
    },

    saveText(item, submit) {
      this.busyId = item.id;
      pitchCustomerApi
        .saveStudyPlanResponse(this.plan.id, item.id, {
          text: this.drafts[item.id] || "",
          submit,
        })
        .then((res) => {
          this.$root.$emit("display-alert", {
            text: this.$t(submit ? "studyPlan.sentOk" : "studyPlan.draftSaved"),
            type: 0,
          });
          this.$emit("update", res.data.data);
        })
        .catch(this.failed)
        .finally(() => {
          this.busyId = "";
        });
    },
  },
};
</script>

<style scoped>
.spt {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --line: var(--dk-line, #ece6ef);
  --surface: var(--dk-surface, #fff);
  --paper: var(--dk-paper, #fbf7fa);
  --grape: var(--dk-grape, #a826e1);
  --gain: var(--dk-teal-deep, #00805f);
  font-family: "Poppins", sans-serif;
  color: var(--ink);
}

.spt__head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px 24px;
  margin-bottom: 14px;
}

.spt__name {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.25;
}

.spt__desc {
  margin: 4px 0 0;
  max-width: 62ch;
  font-size: 0.88rem;
  color: var(--muted);
}

.spt__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.spt__pill {
  padding: 4px 12px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--surface);
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
}

.spt__pill--status {
  border-color: transparent;
  background: var(--paper);
  color: var(--grape);
}

/* ---------- progresso ---------- */
.spt__progress {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 22px;
}

.spt__bar {
  flex: 1;
  height: 10px;
  border-radius: 999px;
  background: var(--line);
  overflow: hidden;
}

.spt__fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--grape);
  transition: width 0.4s ease;
}

.spt__count {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--muted);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* ---------- trilha ---------- */
.spt__trail {
  position: relative;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* O trilho que liga as etapas. */
.spt__trail::before {
  content: "";
  position: absolute;
  top: 12px;
  bottom: 12px;
  left: 11px;
  width: 2px;
  background: var(--line);
}

.spt__stop {
  position: relative;
  display: grid;
  grid-template-columns: 24px minmax(0, 1fr);
  gap: 16px;
  padding-bottom: 14px;
}

.spt__node {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-top: 14px;
  border-radius: 50%;
  border: 2px solid var(--line);
  background: var(--surface);
}

.spt__stop--done .spt__node {
  border-color: var(--gain);
  background: var(--gain);
}

.spt__stop--next .spt__node {
  border-color: var(--grape);
  box-shadow: 0 0 0 4px rgba(168, 38, 225, 0.14);
}

.spt__card {
  min-width: 0;
  padding: 14px 18px 16px;
  border: 1px solid var(--line);
  border-radius: 16px;
  background: var(--surface);
}

.spt__stop--next .spt__card {
  border-color: var(--grape);
}

.spt__stop--done .spt__card {
  background: var(--paper);
}

.spt__kind {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

.spt__kind-icon {
  color: var(--grape) !important;
}

.spt__next {
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--grape);
  color: #fff;
  letter-spacing: 0.04em;
}

.spt__title {
  margin: 0 0 10px;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.spt__title--prompt {
  font-weight: 500;
  white-space: pre-wrap;
}

.spt__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin: 0 0 12px;
  padding: 0;
  list-style: none;
  font-size: 0.84rem;
  color: var(--muted);
}

.spt__facts li {
  display: flex;
  align-items: center;
  gap: 6px;
}

.spt__fact--on {
  color: var(--gain);
  font-weight: 600;
}

.spt__fact--on .v-icon {
  color: var(--gain) !important;
}

.spt__actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
}

.spt__btn {
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  border: 1.5px solid var(--grape);
  border-radius: 999px;
  background: var(--grape);
  color: #fff !important;
  font-family: inherit;
  font-size: 0.84rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}

.spt__btn:hover:not(:disabled) {
  filter: brightness(0.94);
}

.spt__btn:focus-visible {
  outline: 3px solid rgba(168, 38, 225, 0.35);
  outline-offset: 2px;
}

.spt__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.spt__btn--ghost {
  background: transparent;
  color: var(--grape) !important;
}

.spt__check {
  margin: 0;
  padding: 0;
}

.spt__text {
  margin-top: 4px;
}

.spt__hint,
.spt__note {
  margin: 8px 0 0;
  font-size: 0.8rem;
  color: var(--muted);
}

.spt__answer {
  margin: 0 0 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--paper);
  font-size: 0.9rem;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.spt__grade {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.spt__score {
  font-size: 0.9rem;
  color: var(--muted);
}

.spt__score b {
  margin-left: 4px;
  font-size: 1.3rem;
  color: var(--grape);
}

.spt__feedback {
  margin: 0;
  font-size: 0.9rem;
  white-space: pre-wrap;
}

.spt__feedback-k {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}

@media (max-width: 600px) {
  .spt__stop {
    grid-template-columns: 20px minmax(0, 1fr);
    gap: 10px;
  }

  .spt__node {
    width: 20px;
    height: 20px;
  }

  .spt__trail::before {
    left: 9px;
  }

  .spt__progress {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .spt__fill {
    transition: none;
  }
}
</style>
